import type { ButtonProps, MenuProps, SelectProps } from 'antd'
import { Dropdown as AntdDropdown } from 'antd'
import type { DropdownButtonProps as AntdDropdownButtonProps } from 'antd/lib/dropdown'
import { type MouseEventHandler, type ReactNode } from 'react'

import { type HeapAttributesType } from '@/heap/heapAttributes'
import { ChevronDown } from 'lucide-react'
import { Button } from '../buttons/Button'
import { Flex } from '../Flex'

export type DropdownItemsProps = MenuProps['items']

export interface DropdownProps extends Omit<AntdDropdownButtonProps, 'overlay'> {
  id?: string
  dataAttribute?: HeapAttributesType
  leftIcon?: ReactNode
  icon?: ReactNode
  items?: DropdownItemsProps
  label?: string
  loading?: boolean
  type?: ButtonProps['type']
  size?: ButtonProps['size']
  onClick?: MouseEventHandler<HTMLElement>
  onClickMenuItem?: (
    key: string,
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => void
}

// https://github.com/ant-design/ant-design/issues/8268#issuecomment-438419077
const fixFloatingMenuProps: Pick<SelectProps, 'getPopupContainer'> = {
  getPopupContainer: (trigger) => trigger.parentNode,
}

export function Dropdown({
  leftIcon,
  icon = <ChevronDown size={16} />,
  items,
  label,
  loading,
  onClick,
  onClickMenuItem,
  htmlType,
  type,
  size = 'middle',
  dataAttribute,
  ...props
}: DropdownProps) {
  return (
    <AntdDropdown
      menu={{
        items,
        onClick: (e) => {
          e.domEvent.stopPropagation()
          onClickMenuItem?.(e.key, e.domEvent)
        },
      }}
      trigger={['click']}
      {...fixFloatingMenuProps}
      {...props}
    >
      <Button
        aria-label="dropdown-trigger"
        htmlType={htmlType}
        onClick={(e) => {
          e.stopPropagation()
          onClick?.(e)
        }}
        type={type}
        size={size}
        loading={loading}
        dataAttribute={dataAttribute}
      >
        {!loading && (
          <Flex align="center" gap={4}>
            {leftIcon}
            {label && label}
            {icon}
          </Flex>
        )}
      </Button>
    </AntdDropdown>
  )
}
